import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";

const INK = "#1A1614";
const MUTED = "#8A857F";
const HAIRLINE = "#E8E1D3";
const GOLD = "#D4A129";
const CREAM = "#FFFDF7";
const REDBG = "#FDECEC";
const RED = "#DC2626";
const GREENBG = "#E7F6EC";
const GREEN = "#16A34A";

const ReelReview = () => {
  const [reels, setReels] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState("");

  const load = () =>
    apiInstanceFetch
      .get(`reel/pendingReels?limit=100`)
      .then((r) => {
        setReels(r.data || []);
        setTotal(r.total || 0);
      })
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const decide = (reel, status) => {
    if (status === "rejected" && !window.confirm("Reject (hide) this reel? The uploader won't be able to show it.")) return;
    setBusy(reel._id);
    apiInstanceFetch
      .post(`reel/setReelApproval`, { reelId: reel._id, status })
      .then(() => setReels((prev) => prev.filter((x) => x._id !== reel._id)))
      .then(() => setTotal((t) => Math.max(0, t - 1)))
      .catch(() => {})
      .finally(() => setBusy(""));
  };

  // Tag the reel's faith vertical while reviewing — this is what makes it
  // appear in the app's Faith feed.
  const setFaith = (reel, faithCategory) => {
    apiInstanceFetch
      .post(`reel/adminSetFaithCategory`, { reelId: reel._id, faithCategory })
      .then(() =>
        setReels((prev) => prev.map((x) => (x._id === reel._id ? { ...x, faithCategory: faithCategory || null } : x)))
      )
      .catch(() => {});
  };

  const uploader = (r) => {
    if (r.affiliateId) return { name: ((r.affiliateId.firstName || "") + " " + (r.affiliateId.lastName || "")).trim() || "Creator", tag: "Creator", img: r.affiliateId.image };
    if (r.sellerId) return { name: r.sellerId.businessName || ((r.sellerId.firstName || "") + " " + (r.sellerId.lastName || "")).trim() || "Seller", tag: "Seller", img: r.sellerId.image };
    return { name: "Unknown", tag: "—", img: null };
  };

  return (
    <div className="mainCategory">
      <Title name="Reel Review" />

      <div className="d-flex align-items-center" style={{ gap: 10, margin: "16px 0" }}>
        <i className="fa-solid fa-clapperboard" style={{ color: GOLD }} />
        <span style={{ fontWeight: 700, color: INK, fontSize: 15 }}>Awaiting approval ({total})</span>
        <button onClick={load} className="btn btn-sm" style={{ marginLeft: "auto", border: `1px solid ${HAIRLINE}`, color: INK, fontWeight: 600 }}>
          <i className="fa-solid fa-rotate-right" style={{ marginRight: 6 }} />Refresh
        </button>
      </div>

      {reels.length === 0 ? (
        <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, padding: 48, textAlign: "center", color: MUTED }}>
          <i className="fa-solid fa-circle-check" style={{ fontSize: 34, color: GREEN }} />
          <div style={{ marginTop: 12, fontWeight: 600 }}>No reels awaiting review</div>
          <div style={{ fontSize: 13 }}>New uploads will appear here for approval before they go live.</div>
        </div>
      ) : (
        <div className="row">
          {reels.map((r) => {
            const up = uploader(r);
            const product = r.productId && r.productId[0];
            return (
              <div key={r._id} className="col-12 col-md-6 col-xl-4 mb-3">
                <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, overflow: "hidden", height: "100%" }}>
                  {/* thumbnail */}
                  <div style={{ position: "relative", background: "#000", aspectRatio: "9/14", maxHeight: 280, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {r.thumbnail ? (
                      <img src={r.thumbnail} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <i className="fa-solid fa-film" style={{ color: "#444", fontSize: 40 }} />
                    )}
                    {r.video ? (
                      <a href={r.video} target="_blank" rel="noreferrer"
                        style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", textDecoration: "none" }}
                        title="Open video to review">
                        <span style={{ background: "rgba(0,0,0,0.55)", color: "#fff", borderRadius: 999, padding: "8px 14px", fontSize: 13, fontWeight: 700 }}>
                          <i className="fa-solid fa-play" style={{ marginRight: 6 }} />Review video
                        </span>
                      </a>
                    ) : null}
                  </div>

                  {/* meta */}
                  <div style={{ padding: 12 }}>
                    <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: GOLD, background: CREAM, border: `1px solid ${HAIRLINE}`, borderRadius: 6, padding: "1px 7px" }}>{up.tag}</span>
                      <span style={{ fontWeight: 600, color: INK, fontSize: 13 }}>{up.name}</span>
                      <span style={{ marginLeft: "auto", fontSize: 11, color: MUTED }}>{dayjs(r.createdAt).format("DD/MM HH:mm")}</span>
                    </div>
                    {product ? <div style={{ fontSize: 12, color: MUTED, marginBottom: 4 }}>🛍 {product.productName}</div> : null}
                    {/* AI moderation verdict — clean reels auto-approve and never
                        reach this queue; what lands here is flagged or unscanned. */}
                    {r.aiModeration?.verdict ? (
                      <div style={{ fontSize: 11.5, fontWeight: 700, marginBottom: 6,
                        color: r.aiModeration.verdict === "adult" ? "#dc3545" : r.aiModeration.verdict === "borderline" ? "#b8860b" : MUTED }}
                        title={r.aiModeration.worst ? `porn ${r.aiModeration.worst.porn} · hentai ${r.aiModeration.worst.hentai} · sexy ${r.aiModeration.worst.sexy}` : ""}>
                        {r.aiModeration.verdict === "adult" ? "🔴 AI: adult content"
                          : r.aiModeration.verdict === "borderline" ? "🟡 AI: borderline"
                          : r.aiModeration.verdict === "error" ? "⚠ AI scan failed — review manually"
                          : "🟢 AI: clean"}
                        {r.aiModeration.worst ? ` (${Math.round(Math.max(r.aiModeration.worst.porn || 0, r.aiModeration.worst.hentai || 0) * 100)}%)` : ""}
                      </div>
                    ) : (
                      <div style={{ fontSize: 11.5, color: MUTED, marginBottom: 6 }}>⏳ AI scan pending</div>
                    )}
                    {r.description ? (
                      <div style={{ fontSize: 12.5, color: INK, marginBottom: 10, maxHeight: 38, overflow: "hidden" }}>{r.description}</div>
                    ) : <div style={{ height: 6 }} />}

                    <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 11.5, color: MUTED, fontWeight: 600 }}>Faith:</span>
                      <select
                        value={r.faithCategory || ""}
                        onChange={(e) => setFaith(r, e.target.value)}
                        style={{ flex: 1, fontSize: 12.5, padding: "5px 8px", borderRadius: 8, border: `1px solid ${HAIRLINE}`, color: INK, background: CREAM }}
                      >
                        <option value="">Not faith content</option>
                        <option value="christianity">Christianity</option>
                        <option value="islam">Islam</option>
                        <option value="judaism">Judaism</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div className="d-flex" style={{ gap: 8 }}>
                      <button disabled={busy === r._id} onClick={() => decide(r, "approved")}
                        style={{ flex: 1, border: "none", background: GREENBG, color: GREEN, fontWeight: 700, padding: "9px 0", borderRadius: 9, cursor: "pointer" }}>
                        <i className="fa-solid fa-check" style={{ marginRight: 6 }} />Approve
                      </button>
                      <button disabled={busy === r._id} onClick={() => decide(r, "rejected")}
                        style={{ flex: 1, border: "none", background: REDBG, color: RED, fontWeight: 700, padding: "9px 0", borderRadius: 9, cursor: "pointer" }}>
                        <i className="fa-solid fa-xmark" style={{ marginRight: 6 }} />Reject
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReelReview;
